/*
 * cf-vitrine-kit — the pieces the Vitrine and the product-page 3D widget share.
 *
 * Both run the same viewer (cf-vitrine-viewer.js) and painter; this file holds
 * the behaviour that sits on top of them, so the two surfaces can't drift:
 *
 *   TOUR             the "Play" camera tour (photographic angles, slow turns)
 *   stage(o)         Play / orbit-a-part / glow choreography on one viewer
 *   zones(groups)    the colour zones a live shoe has (Leather, Suede, Laces,
 *                    Stitching, Welt, Sole), read off the painted scene
 *   CW_GROUPS        colourway groups by base (All · Unicolor · White · Cream ·
 *   cwGroupOf(a, b)  Brown · Black base · More) from the two body colours
 *   rowFit(o)        Advanced colour: a row per material, or tags over one row?
 *   isWeltGroup(g)   painter group helpers shared with cf-vitrine.js
 *   liningMeshes(scene, P)
 *
 * Users: assets/cf-vitrine-beta.js + cf-vitrine.js (the Vitrine),
 *        assets/cf-product-3d.js + snippets/cf-product-configurator.liquid
 *        (the product page). Must load before them; it needs nothing at load
 *        time (THREE / VitrineData are only touched when called).
 *
 * Plain ES5 IIFE on window, like the rest of the Vitrine.
 */
(function (w) {
  'use strict';

  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

  // ── "Play": photographic angles and zooms, a slow turn on each, on a loop.
  // Model centred at the origin, X = length (toe at +X), Z = width; the
  // mirrored left shoe sits at -Z, so pair shots aim a touch toward -Z.
  var TOUR = [
    { pos: [2.2, 0.7, 1.4],    target: [0, 0, -0.15],    spin: 6,  hold: 4500 }, // hero 3/4
    { pos: [0.2, 0.32, 2.55],  target: [0, 0, -0.25],    spin: -5, hold: 4500 }, // pair in profile
    { pos: [1.15, 0.28, 0.75], target: [0.3, -0.05, 0],  spin: 7,  hold: 4000 }, // toe close-up
    { pos: [-1.9, 0.8, 1.35],  target: [0, 0, -0.2],     spin: -6, hold: 4500 }, // heel 3/4
    { pos: [0.95, 1.75, 0.85], target: [0, 0.05, -0.1],  spin: 6,  hold: 4000 }, // high, into the topline
    { pos: [1.75, -0.02, 1.5], target: [0, -0.18, 0],    spin: -6, hold: 4000 }  // low, along the welt
  ];

  // ── Stage: what the camera does around edits ────────────────────────────
  //   o.viewer()   → the VitrineViewer instance (or null)
  //   o.playing()  → true when the tour should be running (Play picked,
  //                  scene loaded, surface on screen)
  //   o.focus(p)   → optional; swing to a preset ('sole', 'lining', …).
  //                  Defaults to viewer.focusView(p).
  //
  //   apply()            start / stop the tour to match o.playing()
  //   hold(meshes)       stop and frame these (no resume until resumeSoon)
  //   orbit(meshes)      circle a part — three angles, a slow turn on each —
  //                      then back to the tour. Paused: swing round to face it.
  //   resumeSoon(ms)     an edit landed: carry on touring after a beat
  //   glow(meshes, ms)   warm emissive pulse ("this changed")
  //   cancelGlow()       MUST run before every repaint (the painter clones
  //                      materials; a live glow would bake in)
  //   showChange(preset, meshesFn, ms)
  //                      sole / welt / lining changed: swing to it, glow it
  //                      once it's in place, resume touring
  //   isOrbiting()
  function stage(o) {
    var resumeT = null, orbitT = null, glowT = null, orbiting = false, glowJob = null;
    function v() { return o.viewer && o.viewer(); }
    function playing() { return !!(o.playing && o.playing()); }

    function clearTimers() {
      clearTimeout(resumeT); resumeT = null;
      clearTimeout(orbitT); orbitT = null;
      orbiting = false;
    }
    function apply() {
      clearTimers();
      var vv = v();
      if (!vv || !vv.startTour) return;
      if (playing()) vv.startTour(TOUR); else vv.stopTour();
    }
    function hold(meshes) {
      clearTimers();
      var vv = v();
      if (!vv) return;
      if (vv.stopTour) vv.stopTour();
      if (meshes && meshes.length && vv.focusOnObjects) vv.focusOnObjects(meshes);
    }
    function orbit(meshes) {
      var vv = v();
      clearTimers();
      if (!vv || !meshes || !meshes.length) return;
      if (!vv.startTour || typeof THREE === 'undefined') { hold(meshes); return; }
      var box = new THREE.Box3(), tmp = new THREE.Box3();
      meshes.forEach(function (m) { tmp.setFromObject(m); if (!tmp.isEmpty()) box.union(tmp); });
      if (box.isEmpty()) return;
      var c = box.getCenter(new THREE.Vector3()), sz = box.getSize(new THREE.Vector3());
      var dist = clamp(Math.max(sz.x, sz.y, sz.z) * 3.2, 1.25, 2.2);   // close enough to read the part, far enough to see the shoe
      // Start on the side the part faces (out from the shoe's middle).
      var a0 = (Math.abs(c.x) + Math.abs(c.z) > 0.05) ? Math.atan2(c.x, c.z) : 0.6;
      var shots = [[a0, 0.35], [a0 + 0.95, 0.55], [a0 - 0.95, 0.2]].map(function (ae, i) {
        var a = ae[0], e = ae[1];
        return {
          pos: [c.x + dist * Math.sin(a) * Math.cos(e), c.y + dist * Math.sin(e), c.z + dist * Math.cos(a) * Math.cos(e)],
          target: [c.x, c.y, c.z], travel: 1300, hold: 2800, spin: i % 2 ? -5 : 5
        };
      });
      // Paused / hand: still swing round to face the part, then stay put.
      if (!playing()) {
        if (vv.flyTo) vv.flyTo(shots[0].pos, shots[0].target, 1000); else hold(meshes);
        return;
      }
      vv.startTour(shots, { restart: true });
      orbiting = true;
      orbitT = setTimeout(function () {
        orbiting = false;
        if (playing()) vv.startTour(TOUR);
      }, shots.length * (1300 + 2800) - 300);
    }
    function resumeSoon(ms) {
      clearTimers();
      if (playing()) resumeT = setTimeout(apply, ms || 1800);
    }

    function matsOf(m) { return Array.isArray(m.material) ? m.material : [m.material]; }
    function glow(meshes, ms) {
      cancelGlow();
      var saved = new Map();
      (meshes || []).forEach(function (m) {
        matsOf(m).forEach(function (mat) {
          if (mat && mat.emissive && !saved.has(mat)) saved.set(mat, { c: mat.emissive.clone(), i: mat.emissiveIntensity });
        });
      });
      if (!saved.size) return;
      var t0 = performance.now(), dur = ms || 2000;
      function frame(now) {
        if (!glowJob) return;
        var u = (now - t0) / dur;
        if (u >= 1) { cancelGlow(); return; }
        var a = Math.pow(Math.sin(Math.PI * ((u * 2) % 1)), 2);   // two soft swells
        saved.forEach(function (s, mat) { mat.emissive.setRGB(1, 0.78, 0.55); mat.emissiveIntensity = 0.6 * a; });
        glowJob.raf = requestAnimationFrame(frame);
      }
      glowJob = { saved: saved, raf: requestAnimationFrame(frame) };
    }
    function cancelGlow() {
      if (!glowJob) return;
      cancelAnimationFrame(glowJob.raf);
      glowJob.saved.forEach(function (s, mat) { mat.emissive.copy(s.c); mat.emissiveIntensity = s.i; });
      glowJob = null;
    }
    function showChange(preset, meshesFn, ms) {
      hold();
      var vv = v();
      if (preset) { if (o.focus) o.focus(preset); else if (vv && vv.focusView) vv.focusView(preset); }
      clearTimeout(glowT);
      if (meshesFn) glowT = setTimeout(function () { glow(meshesFn()); }, 500);   // after the swap / repaint
      resumeSoon(ms || 3400);
    }

    return {
      apply: apply, hold: hold, orbit: orbit, resumeSoon: resumeSoon,
      glow: glow, cancelGlow: cancelGlow, showChange: showChange,
      isOrbiting: function () { return orbiting; }
    };
  }

  // ── Painter group helpers ───────────────────────────────────────────────
  function isWeltGroup(g) {
    return !!g && g.isSole && String(g.label || '').toLowerCase() === 'welt';
  }
  // The drawn lining: the inside mesh of every mesh base.
  function liningMeshes(scene, P) {
    if (!scene || !P || !P.meshBase || !P.insideMesh) return [];
    var byBase = {};
    scene.traverse(function (obj) {
      if (!obj.isMesh || !obj.name) return;
      var base = P.meshBase(obj.name);
      if (!base) return;
      (byBase[base] = byBase[base] || []).push(obj);
    });
    var out = [];
    Object.keys(byBase).forEach(function (base) {
      var m = P.insideMesh(byBase[base]);
      if (m) out.push(m);
    });
    return out;
  }

  // ── Colour zones ────────────────────────────────────────────────────────
  // Row order; bodies split by detected material (leather / suede).
  var ZONE_ORDER  = ['leather', 'suede', 'laces', 'stitch', 'welt', 'sole'];
  var ZONE_LABELS = { leather: 'Leather', suede: 'Suede', laces: 'Laces', stitch: 'Stitching', welt: 'Welt', sole: 'Sole' };

  function D() { return w.VitrineData || {}; }
  function matOf(m) { return Array.isArray(m.material) ? m.material[0] : m.material; }
  function meshKind(m) {
    var kinds = (D().materialKinds || []).map(function (k) { return k.id; });
    var mat = matOf(m), k = mat && mat.userData && mat.userData.cfKind;
    return kinds.indexOf(k) !== -1 ? k : (D().primaryMaterialKind || 'leather');
  }
  function meshHex(m) {
    var mat = matOf(m);
    if (!mat) return null;
    if (mat.userData && mat.userData.cfHex) return String(mat.userData.cfHex).toUpperCase(); // swatch hex, before display toning
    return mat.color ? '#' + mat.color.getHexString().toUpperCase() : null;
  }
  function majority(meshes) {
    var counts = {}, best = null;
    meshes.forEach(function (m) {
      var h = meshHex(m); if (!h) return;
      counts[h] = (counts[h] || 0) + 1;
      if (!best || counts[h] > counts[best]) best = h;
    });
    return { hex: best, mixed: Object.keys(counts).length > 1 };
  }
  function zoneOfGroup(g) {
    return g.isLaces ? 'laces' : g.isStitch ? 'stitch' : g.isSole ? (isWeltGroup(g) ? 'welt' : 'sole') : null;
  }
  // The zones a live shoe actually has (welt drops out when it's hidden;
  // suede only if the shoe has suede panels), each with its meshes and the
  // colour most of them wear.
  function zones(groups) {
    var by = {};
    (groups || []).forEach(function (g) {
      var z = zoneOfGroup(g);
      g.meshes.forEach(function (m) {
        if (m.visible === false) return;
        var zz = z || meshKind(m);
        if (!ZONE_LABELS[zz]) zz = 'leather';
        (by[zz] = by[zz] || []).push(m);
      });
    });
    return ZONE_ORDER.filter(function (z) { return by[z] && by[z].length; }).map(function (z) {
      var mj = majority(by[z]);
      return { id: z, label: ZONE_LABELS[z], meshes: by[z], hex: mj.hex, mixed: mj.mixed };
    });
  }
  function zoneById(list, id) {
    for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i];
    return null;
  }

  // ── Parts: buckets, overrides, grouping (the build model) ───────────────
  // A build = a colourway + per-part overrides. Overrides are stored per mesh
  // name ({name: {color, label}} / {name: kind}) and resolved per BUCKET (the
  // anatomical prefix, or a shared __laces__ / __stitch__ / __sole__ / __welt__
  // token), so one saved representative restores the whole part. Serialized
  // as x = {hex: [buckets]}, mt = {kind: [buckets]} — the Vitrine's `?cfg=` /
  // `_customization` format, which the product page writes too.
  function P_() { return w.VitrinePainter; }

  // Shown names for parts whose GLB name reads wrong.
  var DISPLAY_NAME_MAP = { 'Aiglet': 'Eyestay', 'aiglet': 'eyestay', 'AIGLET': 'EYESTAY' };
  function displayLabel(label) { return label ? (DISPLAY_NAME_MAP[label] || label) : label; }

  function colorBucket(name) {
    // A bucket key fed back in comes out unchanged (restores re-bucket exactly).
    if (name === '__stitch__' || name === '__laces__' || name === '__sole__' || name === '__welt__') return name;
    var P = P_(), prefix = P.meshPrefix(name);
    if (P.isStitchName(prefix)) return '__stitch__';
    if (P.isLaceName(name))     return '__laces__';
    if (P.isSoleName(prefix))   return '__sole__';
    if (P.isWeltName(prefix))   return '__welt__';
    return prefix;
  }
  // {meshName: v} → {bucket: v}
  function bucketMap(byMesh) {
    var m = {};
    Object.keys(byMesh || {}).forEach(function (name) { m[colorBucket(name)] = byMesh[name]; });
    return m;
  }
  function serializeOverrides(colors, mats) {
    var out = {}, seen = {}, byColor = {}, byKind = {}, c = false, k = false;
    Object.keys(colors || {}).forEach(function (name) {
      var b = colorBucket(name), hex = colors[name].color, key = 'c' + b + '|' + hex;
      if (seen[key]) return; seen[key] = true; c = true;
      (byColor[hex] = byColor[hex] || []).push(b);
    });
    Object.keys(mats || {}).forEach(function (name) {
      var b = colorBucket(name), kind = mats[name], key = 'm' + b + '|' + kind;
      if (seen[key]) return; seen[key] = true; k = true;
      (byKind[kind] = byKind[kind] || []).push(b);
    });
    if (c) out.x = byColor;
    if (k) out.mt = byKind;
    return out;
  }
  // Inverse of serializeOverrides. Also accepts the legacy flat {name: hex}.
  function parseOverrides(x, mt) {
    var DD = D(), colors = {}, mats = {};
    function lab(h) { return (DD.hexLabel && DD.hexLabel[String(h).toUpperCase()]) || h; }
    Object.keys(x || {}).forEach(function (k) {
      var v = x[k];
      if (Array.isArray(v)) v.forEach(function (n) { colors[n] = { color: k, label: lab(k) }; });
      else if (typeof v === 'string') colors[k] = { color: v, label: lab(v) };
    });
    Object.keys(mt || {}).forEach(function (kind) {
      (mt[kind] || []).forEach(function (n) { mats[n] = kind; });
    });
    return { colors: colors, mats: mats };
  }

  function explicitPartOverrideKind(shoeId, prefix) {
    var ov = D().partKindOverrides && D().partKindOverrides[shoeId];
    if (!ov) return null;
    function norm(s) { return String(s || '').replace(/[^a-z]/gi, '').toLowerCase(); }
    var p = norm(prefix);
    for (var k in ov) { if (norm(k) === p) return ov[k]; }
    return null;
  }
  // Body (upper) meshes — the ones a leather/suede swap applies to.
  function isBodyMesh(obj, shoeId) {
    var P = P_();
    if (!obj.isMesh || !obj.name) return false;
    if (P.isLaceName(obj.name)) return false;
    var prefix = P.meshPrefix(obj.name);
    if (!prefix) return false;
    if (P.isStitchName(prefix) || P.isSoleName(prefix) || P.isWeltName(prefix)) return false;
    if (/^Part([.\s_]|$)/i.test(prefix) && !explicitPartOverrideKind(shoeId, prefix)) return false;
    return true;
  }
  function snapshotMaterialProps(m) {
    if (!m) return null;
    function vec2(v) { return v ? { x: v.x, y: v.y } : null; }
    return {
      roughness:        typeof m.roughness === 'number' ? m.roughness : null,
      metalness:        typeof m.metalness === 'number' ? m.metalness : null,
      bumpScale:        typeof m.bumpScale === 'number' ? m.bumpScale : null,
      aoMapIntensity:   typeof m.aoMapIntensity === 'number' ? m.aoMapIntensity : null,
      // Brightness under the scene's image-based lighting — without this a
      // suede→leather swap can render noticeably darker/brighter than the source.
      envMapIntensity:   typeof m.envMapIntensity === 'number' ? m.envMapIntensity : null,
      emissiveIntensity: typeof m.emissiveIntensity === 'number' ? m.emissiveIntensity : null,
      normalMap:        m.normalMap || null,
      normalScale:      vec2(m.normalScale),
      roughnessMap:     m.roughnessMap || null,
      metalnessMap:     m.metalnessMap || null,
      bumpMap:          m.bumpMap || null,
      aoMap:            m.aoMap || null
    };
  }
  // The shoe's own texture + props per kind, from the OUTSIDE (N-1) mesh of a
  // part — never an inside layer (different UV scale, shinier: swapped leather
  // came out zoomed and over-shiny when sampled from the wrong layer).
  // Run on a freshly loaded model, before any swap.
  function kindSources(scene, shoeId) {
    var P = P_(), byPrefix = {}, out = {};
    if (!scene) return out;
    scene.traverse(function (obj) {
      if (!isBodyMesh(obj, shoeId)) return;
      var prefix = P.meshPrefix(obj.name);
      if (prefix) (byPrefix[prefix] = byPrefix[prefix] || []).push(obj);
    });
    Object.keys(byPrefix).forEach(function (prefix) {
      var outside = P.outsideMesh(byPrefix[prefix]);
      if (!outside) return;
      var m = matOf(outside);
      if (!m || !m.map) return;
      var kind = m.userData && m.userData.cfKind;
      if (kind && !out[kind]) out[kind] = { tex: m.map, props: snapshotMaterialProps(m) };
    });
    return out;
  }

  function dropHiddenMeshes(groups) {
    var out = [];
    groups.forEach(function (g) {
      var vis = g.meshes.filter(function (m) { return m.visible !== false; });
      if (!vis.length) return;
      if (vis.length !== g.meshes.length) g.meshes = vis;
      out.push(g);
    });
    return out;
  }
  // Painter groups for a parts mode: 'simple' = by texture, 'advanced' = every
  // part (prefix) on its own. Hidden meshes (welt off) drop out.
  function partGroups(scene, partsMode) {
    var P = P_();
    return dropHiddenMeshes(partsMode === 'advanced' ? P.groupByPrefix(scene) : P.groupByTexture(scene));
  }
  // Repaint step 3 + 3b: per-part colour overrides, then leather/suede swaps,
  // both resolved by bucket. o: { colors, mats, kindSwapSource(kind), sideMeshes }
  function applyOverrides(groups, o) {
    var P = P_(), side = o.sideMeshes || [];
    if (Object.keys(o.colors || {}).length) {
      var bColor = bucketMap(o.colors);
      groups.forEach(function (g) {
        // Batch by colour: one material clone per colour per group.
        var byColor = {};
        g.meshes.forEach(function (mesh) {
          var ov = mesh.name && bColor[colorBucket(mesh.name)];
          if (!ov) return;
          (byColor[ov.color] = byColor[ov.color] || { swatch: ov, meshes: [] }).meshes.push(mesh);
        });
        var colors = Object.keys(byColor);
        if (!colors.length) return;
        colors.forEach(function (c) { P.paintMeshes(byColor[c].meshes, c); });
        g.lastColor = byColor[colors[0]].swatch;
        if (g.isSole && side.length) P.paintMeshes(side, colors[0]);
      });
    }
    // After paintMeshes clones materials, .map still points at the source
    // texture — applyMaterialKind swaps it, keeping the colour set above.
    if (Object.keys(o.mats || {}).length && o.kindSwapSource) {
      var bMat = bucketMap(o.mats);
      groups.forEach(function (g) {
        var kind = null;
        for (var i = 0; i < g.meshes.length && !kind; i++) {
          var n = g.meshes[i].name;
          if (n && bMat[colorBucket(n)]) kind = bMat[colorBucket(n)];
        }
        var src = kind && o.kindSwapSource(kind);
        if (src) P.applyMaterialKind(g.meshes, kind, src.tex, g.id, src.props);
      });
    }
  }
  // Snapshot a part's current colour as an override before swapping its
  // material (else the next repaint paints the swapped mesh with the colour
  // its new kind gets in the colourway), then swap. Records the kind.
  function swapPartMaterial(g, kind, src, colors, mats) {
    var P = P_(), DD = D();
    g.meshes.forEach(function (m) {
      if (!m.name) return;
      var mat = matOf(m);
      if (mat && mat.color) {
        var hex = '#' + mat.color.getHexString().toUpperCase();
        colors[m.name] = { color: hex, label: (DD.hexLabel && DD.hexLabel[hex]) || hex };
      }
    });
    P.applyMaterialKind(g.meshes, kind, src.tex, g.id, src.props);
    g.meshes.forEach(function (m) { if (m.name) mats[m.name] = kind; });
  }

  function orderSpecialsToEnd(groups) {
    var bodies = [], soles = [], laces = [], welts = [], stitches = [];
    groups.forEach(function (g) {
      if (g.isLaces)       laces.push(g);
      else if (g.isStitch) stitches.push(g);
      else if (g.isSole)   ((g.label || '').toLowerCase() === 'welt' ? welts : soles).push(g);
      else                 bodies.push(g);
    });
    return bodies.concat(soles, laces, welts, stitches);
  }
  // Simple mode: body meshes regrouped by (material kind, current colour) —
  // one row per kind, split by colour when edits made it two-tone.
  function regroupSimpleBodies(groups) {
    var DD = D(), bodyMeshes = [], sole = null, laces = null, welt = null, stitch = null;
    groups.forEach(function (g) {
      if (g.isLaces) { laces = g; return; }
      if (g.isStitch) { stitch = g; return; }
      if (g.isSole) { if ((g.label || '').toLowerCase() === 'welt') welt = g; else sole = g; return; }
      g.meshes.forEach(function (m) { bodyMeshes.push(m); });
    });
    var byKey = {}, order = [];
    bodyMeshes.forEach(function (mesh) {
      var mat = matOf(mesh);
      var hex  = (mat && mat.color) ? '#' + mat.color.getHexString().toUpperCase() : '';
      var kind = (mat && mat.userData && mat.userData.cfKind) || '';
      var key  = kind + '|' + hex;
      if (!byKey[key]) { byKey[key] = { kind: kind, hex: hex, meshes: [] }; order.push(key); }
      byKey[key].meshes.push(mesh);
    });
    var kindOrder = (DD.materialKinds || []).map(function (mk) { return mk.id; });
    order.sort(function (a, b) {
      var ka = byKey[a].kind, kb = byKey[b].kind;
      var ia = kindOrder.indexOf(ka); if (ia < 0) ia = kindOrder.length;
      var ib = kindOrder.indexOf(kb); if (ib < 0) ib = kindOrder.length;
      if (ia !== ib) return ia - ib;
      return byKey[a].hex.localeCompare(byKey[b].hex);
    });
    var sameKind = {};
    order.forEach(function (k) { sameKind[byKey[k].kind] = (sameKind[byKey[k].kind] || 0) + 1; });
    var rows = order.map(function (key, i) {
      var c = byKey[key];
      var kindLabel = c.kind ? c.kind.charAt(0).toUpperCase() + c.kind.slice(1) : 'Material';
      var hl = (DD.hexLabel && DD.hexLabel[c.hex]) || c.hex;
      return {
        id: 'simple_' + i, label: sameKind[c.kind] > 1 ? kindLabel + ' · ' + hl : kindLabel,
        meshes: c.meshes, isLaces: false, isStitch: false, isSole: false,
        textureName: '', textureKey: 'simple_' + i, lastColor: { color: c.hex, label: hl }
      };
    });
    var tail = [];
    if (sole) tail.push(sole);
    if (laces) tail.push(laces);
    if (welt) tail.push(welt);
    if (stitch) tail.push(stitch);
    return rows.concat(tail);
  }
  function finishGroups(groups, partsMode) {
    return partsMode === 'simple' ? regroupSimpleBodies(groups) : orderSpecialsToEnd(groups);
  }

  // Colourways in the order a shoe shows them (its picks first).
  var SHOE_COLORWAY_ORDER = {
    club:    ['solid-white', 'season-spring', 'f1-pole', 'solid-cream'],
    wave:    ['solid-white', 'season-spring', 'f1-pole', 'solid-cream'],
    surplus: ['solid-white', 'season-spring', 'f1-pole', 'solid-cream'],
    coop:    ['solid-white', 'season-spring', 'f1-pole', 'solid-cream'],
    city:    ['solid-brown', 'solid-white', 'solid-black'],
    desert:  ['solid-brown', 'solid-white', 'solid-black']
  };
  function orderedColorways(shoeId) {
    var all = D().colorways || [], rest = {}, out = [];
    all.forEach(function (c) { rest[c.id] = c; });
    (SHOE_COLORWAY_ORDER[shoeId] || []).forEach(function (id) { if (rest[id]) { out.push(rest[id]); delete rest[id]; } });
    all.forEach(function (c) { if (rest[c.id]) out.push(c); });
    return out;
  }

  // ── Colourway groups, by base ───────────────────────────────────────────
  // 'all' is first and the default: every colourway, in the curated order.
  // cwGroupOf() never returns it.
  var CW_GROUPS = [
    { id: 'all',   label: 'All' },
    { id: 'uni',   label: 'Unicolor' },
    { id: 'white', label: 'White base', hex: '#FFFFFF' },
    { id: 'cream', label: 'Cream base', hex: '#EDE0C8' },
    { id: 'brown', label: 'Brown base', hex: '#7B4F2E' },
    { id: 'black', label: 'Black base', hex: '#1A1A1A' },
    { id: 'more',  label: 'More' }
  ];
  // a = body1 (leather) colour, b = body2 (suede) colour.
  function cwGroupOf(a, b) {
    a = String(a || '').toUpperCase(); b = String(b || a).toUpperCase();
    if (a === b) return 'uni';
    for (var i = 0; i < CW_GROUPS.length; i++) if (CW_GROUPS[i].hex === a) return CW_GROUPS[i].id;
    return 'more';
  }

  // ── Advanced colour: a row per material, or tags over one shared row ────
  // Rows when a label + every swatch fit across `width` at `min` px or more
  // (swatches sized to fill, up to `max`) AND `rows` rows fit in `maxH`.
  //   o: { width, n, labelW, gap, min, max, rows, rowH, maxH }
  function rowFit(o) {
    var sw = Math.min(o.max, Math.floor((o.width - o.labelW - (o.n - 1) * o.gap) / o.n));
    var ok = sw >= o.min && (o.maxH == null || o.rows * o.rowH <= o.maxH);
    return { layout: ok ? 'rows' : 'tags', sw: sw };
  }

  w.VitrineKit = {
    TOUR: TOUR,
    stage: stage,
    isWeltGroup: isWeltGroup,
    liningMeshes: liningMeshes,
    ZONE_ORDER: ZONE_ORDER,
    ZONE_LABELS: ZONE_LABELS,
    meshKind: meshKind,
    meshHex: meshHex,
    majority: majority,
    zoneOfGroup: zoneOfGroup,
    zones: zones,
    zoneById: zoneById,
    CW_GROUPS: CW_GROUPS,
    cwGroupOf: cwGroupOf,
    rowFit: rowFit,
    displayLabel: displayLabel,
    colorBucket: colorBucket,
    bucketMap: bucketMap,
    serializeOverrides: serializeOverrides,
    parseOverrides: parseOverrides,
    explicitPartOverrideKind: explicitPartOverrideKind,
    isBodyMesh: isBodyMesh,
    snapshotMaterialProps: snapshotMaterialProps,
    kindSources: kindSources,
    dropHiddenMeshes: dropHiddenMeshes,
    partGroups: partGroups,
    applyOverrides: applyOverrides,
    swapPartMaterial: swapPartMaterial,
    orderSpecialsToEnd: orderSpecialsToEnd,
    regroupSimpleBodies: regroupSimpleBodies,
    finishGroups: finishGroups,
    orderedColorways: orderedColorways,
    SHOE_COLORWAY_ORDER: SHOE_COLORWAY_ORDER
  };
})(window);
